import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Trophy, Zap, X, Sparkles } from "lucide-react";
import { playCounterClickSound } from "@/utils/sounds";

const STREAK_KEY = "wwi_fire_counter";

// Milestones - special celebrations at certain counts
// Note: 10, 25, 50, 100-900 have custom modals in AppPage.jsx
// This array is only used for progress tracking, not celebrations
const MILESTONES = [
  { count: 10, title: "Ten Tasks!", emoji: "🎉", message: "You're on fire!", isBig: true, hasCustomModal: true },
  { count: 25, title: "Quarter Century", emoji: "⚡", message: "Incredible momentum!", hasCustomModal: true },
  { count: 50, title: "Halfway to 100", emoji: "🏆", message: "Keep going!", hasCustomModal: true },
  { count: 75, title: "Three Quarters", emoji: "👑", message: "Almost there!" },
  { count: 100, title: "Century!", emoji: "💯", message: "One hundred tasks!", hasCustomModal: true },
  { count: 200, title: "Two Hundred", emoji: "🔥", message: "Unstoppable!", hasCustomModal: true },
  { count: 300, title: "Three Hundred", emoji: "⭐", message: "You're a legend!", hasCustomModal: true },
  { count: 400, title: "Four Hundred", emoji: "💎", message: "Diamond focus!", hasCustomModal: true },
  { count: 500, title: "Five Hundred", emoji: "🚀", message: "Halfway to max!", hasCustomModal: true },
  { count: 600, title: "Six Hundred", emoji: "🌟", message: "Stellar progress!", hasCustomModal: true },
  { count: 700, title: "Seven Hundred", emoji: "🏅", message: "Champion level!" },
  { count: 800, title: "Eight Hundred", emoji: "👑", message: "Nearly maxed!" },
  { count: 900, title: "Nine Hundred", emoji: "🎯", message: "One more century!", hasCustomModal: true },
];

// Get the next milestone and progress toward it
const getMilestoneProgress = (count) => {
  const sorted = [...MILESTONES].sort((a, b) => a.count - b.count);
  let prevCount = 0;
  for (const m of sorted) {
    if (count < m.count) {
      const range = m.count - prevCount;
      const progress = count - prevCount;
      return { next: m.count, progress: progress / range, current: count, label: `${count} / ${m.count}` };
    }
    prevCount = m.count;
  }
  // Past all milestones (900+) - progress toward 999
  return { next: 999, progress: (count - 900) / 99, current: count, label: `${count}` };
};

// Load counter data from localStorage
const loadCounterData = () => {
  if (typeof window === 'undefined') return { count: 0, totalCompleted: 0, highScore: 0, cycleCount: 1 };
  
  try {
    const stored = localStorage.getItem(STREAK_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Ensure cycleCount exists (migration for existing users)
      return {
        ...data,
        cycleCount: data.cycleCount || 1
      };
    }
  } catch (e) {
    console.error('Error loading counter data:', e);
  }
  
  return { count: 0, totalCompleted: 0, highScore: 0, cycleCount: 1 };
};

// Save counter data to localStorage
const saveCounterData = (data) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STREAK_KEY, JSON.stringify(data));
  }
};

// Hook to manage fire counter
export const useStreak = () => {
  const [counterData, setCounterData] = useState(loadCounterData);
  const [newMilestone, setNewMilestone] = useState(null);
  const [showBigCelebration, setShowBigCelebration] = useState(false);

  // Load on mount
  useEffect(() => {
    setCounterData(loadCounterData());
  }, []);

  // Record a completion - increments by `weight` (Important tasks count as 2)
  const recordCompletion = useCallback((weight = 1) => {
    setCounterData(prev => {
      let newCount = prev.count;
      let newTotal = prev.totalCompleted;
      let newCycleCount = prev.cycleCount || 1;
      let triggeredMilestone = null;

      // Step through each unit so milestones/rollovers aren't skipped when weight > 1
      for (let i = 0; i < weight; i++) {
        newCount += 1;
        newTotal += 1;

        // Hard reset at 999 - start fresh from 1
        if (newCount > 999) {
          newCount = 1;
          newCycleCount = newCycleCount + 1;
        }

        // Check for milestone - skip if it has a custom modal (handled in AppPage.jsx)
        const milestone = MILESTONES.find(m => m.count === newCount);
        if (milestone && !milestone.hasCustomModal) {
          triggeredMilestone = milestone;
        }
      }

      const newHighScore = Math.max(prev.highScore, newCount);

      if (triggeredMilestone) {
        setNewMilestone(triggeredMilestone);
        if (triggeredMilestone.isBig) {
          setShowBigCelebration(true);
        }
      }

      const newData = {
        count: newCount,
        totalCompleted: newTotal,
        highScore: newHighScore,
        cycleCount: newCycleCount,
      };

      saveCounterData(newData);
      return newData;
    });
  }, []);

  // Decrement count when undoing a completion (moving from Done back)
  // Accepts weight so undoing an Important task removes 2
  const decrementCount = useCallback((weight = 1) => {
    setCounterData(prev => {
      // Don't go below 0
      const newCount = Math.max(0, prev.count - weight);

      const newData = {
        count: newCount,
        totalCompleted: prev.totalCompleted, // Don't decrement total
        highScore: prev.highScore, // Keep high score
        cycleCount: prev.cycleCount || 1,
      };

      saveCounterData(newData);
      return newData;
    });
  }, []);

  const dismissMilestone = useCallback(() => {
    setNewMilestone(null);
    setShowBigCelebration(false);
  }, []);

  // Set fire counter from cloud data (for sync)
  const setFireCounterFromCloud = useCallback((cloudData) => {
    if (cloudData) {
      setCounterData({
        count: cloudData.count || 0,
        totalCompleted: cloudData.totalCompleted || 0,
        highScore: cloudData.highScore || 0,
        cycleCount: cloudData.cycleCount || 1,
      });
    }
  }, []);

  // Reset cycle count - called after 100-task completion
  // Resets count to 0, increments cycle, preserves totalCompleted and highScore
  const resetCycleCount = useCallback(() => {
    setCounterData(prev => {
      const newCycleCount = (prev.cycleCount || 1) + 1;
      const newData = {
        count: 0,
        totalCompleted: prev.totalCompleted, // Keep lifetime total
        highScore: prev.highScore, // Keep high score
        cycleCount: newCycleCount,
      };
      
      saveCounterData(newData);
      return newData;
    });
  }, []);

  return {
    currentStreak: counterData.count,
    longestStreak: counterData.highScore,
    totalCompleted: counterData.totalCompleted,
    cycleCount: counterData.cycleCount || 1,
    completedToday: false, // Not tracking daily anymore
    isAtRisk: false, // No risk in task-based counter
    recordCompletion,
    decrementCount,
    resetCycleCount,
    newMilestone,
    showBigCelebration,
    dismissMilestone,
    setFireCounterFromCloud,
  };
};

// Flame intensity based on count - now scales up to 999
const getFlameConfig = (count) => {
  if (count === 0) return { color: "text-stone-300", size: 18, intensity: 0 };
  if (count < 5) return { color: "text-orange-400", size: 18, intensity: 1 };
  if (count < 10) return { color: "text-orange-500", size: 20, intensity: 2 };
  if (count < 25) return { color: "text-orange-600", size: 22, intensity: 3 };
  if (count < 50) return { color: "text-red-500", size: 24, intensity: 4 };
  if (count < 100) return { color: "text-red-600", size: 26, intensity: 5 };
  if (count < 200) return { color: "text-red-600", size: 27, intensity: 6 };
  if (count < 500) return { color: "text-red-700", size: 28, intensity: 7 };
  return { color: "text-red-700", size: 30, intensity: 8 }; // 500+
};

// Subtle flame growth based on total completed tasks
const getFlameScale = (total) => {
  if (total >= 100) return { size: 22, glow: '0 0 6px rgba(249,115,22,0.25)' };
  if (total >= 50) return { size: 20, glow: '0 0 4px rgba(249,115,22,0.18)' };
  if (total >= 25) return { size: 19, glow: '0 0 3px rgba(249,115,22,0.12)' };
  return { size: 18, glow: 'none' };
};

// Flame evolution based on cycle count - subtle visual progression
const getFlameEvolution = (cycleCount) => {
  if (cycleCount >= 5) {
    // Cycle 5+: very soft pulsing energy
    return { 
      brightness: 1.15, 
      outerGlow: '0 0 12px rgba(249,115,22,0.35)', 
      hasPulse: true,
      coreGlow: '0 0 4px rgba(255,255,255,0.4)'
    };
  }
  if (cycleCount >= 3) {
    // Cycle 3: subtle outer glow
    return { 
      brightness: 1.1, 
      outerGlow: '0 0 8px rgba(249,115,22,0.25)', 
      hasPulse: false,
      coreGlow: '0 0 2px rgba(255,255,255,0.25)'
    };
  }
  if (cycleCount >= 2) {
    // Cycle 2: slightly brighter core
    return { 
      brightness: 1.05, 
      outerGlow: 'none', 
      hasPulse: false,
      coreGlow: '0 0 2px rgba(255,255,255,0.15)'
    };
  }
  // Cycle 1: standard flame
  return { brightness: 1, outerGlow: 'none', hasPulse: false, coreGlow: 'none' };
};

// Fire counter badge — compact inline momentum marker
export const StreakBadge = ({ streak, totalCompleted = 0, cycleCount = 1, onClick, justCompleted = false, particlesActive = false }) => {
  const mp = getMilestoneProgress(streak);
  const flameGrowth = getFlameScale(totalCompleted);
  const flameEvolution = getFlameEvolution(cycleCount);
  
  return (
    <div className="flex flex-col items-center">
      <motion.button
        onClick={onClick}
        className="flex items-center gap-1 px-2 py-1 rounded-full transition-all hover:bg-orange-50"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="streak-badge"
      >
        {/* Flame icon with "feeding" glow on completion and cycle evolution */}
        <motion.div
          className="relative -top-[2px]"
          style={{ 
            filter: flameGrowth.glow !== 'none' ? `drop-shadow(${flameGrowth.glow})` : undefined,
          }}
          animate={justCompleted ? { 
            scale: [1, 1.15, 1.2, 1.1, 1],
            y: [0, -2, -1, 0]
          } : particlesActive ? {
            // Gentle bloom while particles float up
            scale: [1, 1.08, 1.12, 1.08, 1.1],
            y: [0, -1, -1.5, -1, 0]
          } : flameEvolution.hasPulse ? {
            scale: [1, 1.03, 1],
          } : { scale: 1, y: 0 }}
          transition={justCompleted ? { 
            duration: 0.6, 
            ease: [0.25, 0.1, 0.25, 1],
            times: [0, 0.3, 0.5, 0.7, 1]
          } : particlesActive ? {
            // Slow bloom that matches particle float duration
            duration: 2.5,
            ease: "easeInOut",
            times: [0, 0.3, 0.6, 0.85, 1]
          } : flameEvolution.hasPulse ? {
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut"
          } : {}}
        >
          <motion.div
            animate={justCompleted ? { 
              filter: [
                'drop-shadow(0 0 0px rgba(249,115,22,0))',
                'drop-shadow(0 0 6px rgba(249,115,22,0.3))',
                'drop-shadow(0 0 14px rgba(249,115,22,0.6))',
                'drop-shadow(0 0 10px rgba(249,115,22,0.4))',
                'drop-shadow(0 0 0px rgba(249,115,22,0))'
              ]
            } : particlesActive ? {
              // Gentle glow while particles approach
              filter: [
                'drop-shadow(0 0 0px rgba(249,115,22,0))',
                'drop-shadow(0 0 4px rgba(249,115,22,0.2))',
                'drop-shadow(0 0 8px rgba(249,115,22,0.35))',
                'drop-shadow(0 0 6px rgba(249,115,22,0.25))',
                'drop-shadow(0 0 4px rgba(249,115,22,0.15))'
              ]
            } : {}}
            transition={justCompleted ? { 
              duration: 0.7, 
              ease: [0.25, 0.1, 0.25, 1],
              times: [0, 0.2, 0.45, 0.7, 1]
            } : particlesActive ? {
              duration: 2.5,
              ease: "easeInOut",
              times: [0, 0.2, 0.5, 0.75, 1]
            } : {}}
            style={{
              filter: flameEvolution.outerGlow !== 'none' ? `drop-shadow(${flameEvolution.outerGlow})` : undefined,
            }}
          >
            <Flame 
              className="text-orange-500 transition-all duration-500" 
              size={flameGrowth.size}
              style={{
                filter: flameEvolution.brightness > 1 ? `brightness(${flameEvolution.brightness})` : undefined,
              }}
            />
          </motion.div>
        </motion.div>
        
        {/* Number with roll-up transition when fed */}
        <div className="relative overflow-hidden h-[1.2em] flex items-center" style={{ marginTop: '0.5px' }}>
          <motion.span 
            key={streak}
            className="text-[15px] font-semibold text-orange-500 leading-none block"
            initial={justCompleted ? { y: 12, opacity: 0 } : { y: 0, opacity: 1 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ 
              duration: 0.35, 
              ease: [0.34, 1.56, 0.64, 1], // Bouncy ease for satisfying roll
            }}
            onAnimationStart={() => {
              // Play subtle click when counter rolls after completion
              if (justCompleted) {
                playCounterClickSound();
              }
            }}
          >
            {streak}
          </motion.span>
        </div>
        <span className="text-[15px] font-normal text-[#C4C4C4] leading-none flex items-center">/{streak < 10 ? '10' : '100'}</span>
      </motion.button>
      
      {/* Cycle indicator - only show if past cycle 1 */}
      {cycleCount > 1 && (
        <span 
          className="text-[10px] text-stone-400 lowercase leading-none -mt-0.5"
          style={{ opacity: 0.5 }}
        >
          cycle {cycleCount}
        </span>
      )}
    </div>
  );
};

// Fire counter detail panel
export const StreakPanel = ({ isOpen, onClose, currentStreak, longestStreak, totalCompleted }) => {
  if (!isOpen) return null;
  
  const nextMilestone = MILESTONES.find(m => m.count > currentStreak);
  const toNext = nextMilestone ? nextMilestone.count - currentStreak : null;
  const toReset = 99 - currentStreak;
  
  return (
    <div 
      className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-[#27272A]">Fire Counter</h2>
          <button
            onClick={onClose}
            data-testid="streak-panel-close"
            className="p-2 text-stone-400 hover:text-stone-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Main counter display */}
        <div className="text-center py-6">
          <motion.div
            className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-orange-100 to-red-100 mb-4"
            animate={{ 
              boxShadow: currentStreak > 0 
                ? ["0 0 0 0 rgba(251, 146, 60, 0)", "0 0 30px 10px rgba(251, 146, 60, 0.3)", "0 0 0 0 rgba(251, 146, 60, 0)"]
                : "none"
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="text-center">
              <Flame className={`mx-auto ${getFlameConfig(currentStreak).color}`} size={36} />
              <span className="text-3xl font-bold text-orange-600">{currentStreak}</span>
            </div>
          </motion.div>
          
          <p className="text-lg font-semibold text-[#27272A]">
            {currentStreak === 0 ? "Complete a task!" : `${currentStreak} tasks completed`}
          </p>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Resets at 99
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-[#FDFBF7] rounded-xl p-4 text-center">
            <Trophy className="mx-auto text-amber-500 mb-2" size={24} />
            <p className="text-2xl font-bold text-[#27272A]">{longestStreak}</p>
            <p className="text-xs text-[#A1A1AA]">High score</p>
          </div>
          <div className="bg-[#FDFBF7] rounded-xl p-4 text-center">
            <Zap className="mx-auto text-[#3DC4D0] mb-2" size={24} />
            <p className="text-2xl font-bold text-[#27272A]">{totalCompleted || 0}</p>
            <p className="text-xs text-[#A1A1AA]">Total completed</p>
          </div>
        </div>

        {/* Progress to next milestone or reset */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
          {nextMilestone ? (
            <>
              <p className="text-sm font-medium text-orange-800">
                <span className="text-lg mr-2">{nextMilestone.emoji}</span>
                Next: {nextMilestone.title} at {nextMilestone.count}
              </p>
              <div className="mt-2 bg-white rounded-full h-2 overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-orange-400 to-red-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStreak / nextMilestone.count) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-xs text-orange-600 mt-1">
                {toNext} more to go!
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-orange-800">
                <span className="text-lg mr-2">🔄</span>
                Reset coming at 99!
              </p>
              <div className="mt-2 bg-white rounded-full h-2 overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-orange-400 to-red-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStreak / 99) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <p className="text-xs text-orange-600 mt-1">
                {toReset} more until reset!
              </p>
            </>
          )}
        </div>

        {/* Motivation */}
        <p className="mt-4 text-xs text-center text-[#A1A1AA]">
          Every completed task adds to your fire!
        </p>
      </motion.div>
    </div>
  );
};

// Big celebration at milestone 10 - more confetti, upbeat!
export const BigCelebration = ({ isActive, onComplete }) => {
  useEffect(() => {
    if (isActive) {
      // Play upbeat sound
      try {
        const audio = new Audio('/sounds/milestone.mp3');
        audio.volume = 0.5;
        audio.play().catch((err) => {
          console.debug('Milestone sound autoplay blocked:', err?.message);
        });
      } catch (e) {
        console.warn('Milestone sound failed to load:', e.message);
      }
      
      // Auto-dismiss after 4 seconds
      const timer = setTimeout(onComplete, 4000);
      return () => clearTimeout(timer);
    }
  }, [isActive, onComplete]);

  if (!isActive) return null;

  // Generate lots of confetti particles
  const particles = Array.from({ length: 60 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2,
    size: 8 + Math.random() * 12,
    color: ['#F58227', '#3DC4D0', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1'][Math.floor(Math.random() * 6)],
  }));

  return (
    <div className="fixed inset-0 z-[70] pointer-events-none overflow-hidden">
      {/* Confetti particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: -20,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
          }}
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{ 
            y: window.innerHeight + 50,
            opacity: [1, 1, 0],
            rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
            x: (Math.random() - 0.5) * 200,
          }}
          transition={{ 
            duration: p.duration,
            delay: p.delay,
            ease: "easeOut"
          }}
        />
      ))}
      
      {/* Center celebration message */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onComplete}
      >
        <motion.div
          className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-sm mx-4"
          initial={{ scale: 0.5, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <motion.div
            className="text-7xl mb-4"
            animate={{ 
              scale: [1, 1.3, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ duration: 0.6, repeat: 3 }}
          >
            🎉
          </motion.div>
          <motion.h2
            className="text-3xl font-bold text-[#27272A] mb-2"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            TEN TASKS!
          </motion.h2>
          <motion.p
            className="text-xl text-orange-600 font-semibold mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            You're on fire! 🔥
          </motion.p>
          <motion.div
            className="flex justify-center gap-2"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Sparkles className="text-amber-500" size={24} />
            <Flame className="text-orange-500" size={24} />
            <Sparkles className="text-amber-500" size={24} />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Regular milestone celebration modal
export const MilestoneCelebration = ({ milestone, onDismiss }) => {
  if (!milestone || milestone.isBig) return null; // Big milestones use BigCelebration
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onDismiss}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        exit={{ opacity: 0, scale: 0.5 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl text-center"
      >
        <motion.div 
          className="text-7xl mb-4"
          animate={{ 
            scale: [1, 1.3, 1],
            rotate: [0, 10, -10, 0]
          }}
          transition={{ duration: 0.6, repeat: 2 }}
        >
          {milestone.emoji}
        </motion.div>
        
        <motion.h2 
          className="text-2xl font-bold text-[#27272A] mb-2"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {milestone.title}!
        </motion.h2>
        
        <motion.p
          className="text-[#52525B] mb-2"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {milestone.count} tasks completed
        </motion.p>
        
        <motion.p
          className="text-orange-600 font-medium mb-6"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {milestone.message}
        </motion.p>
        
        <motion.button
          onClick={onDismiss}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-xl transition-shadow"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Keep it going! 🔥
        </motion.button>
      </motion.div>
    </div>
  );
};

export default StreakBadge;
